import { useCallback, useEffect, useState } from "react";
import { AxiosInstance } from "../services/Axios";

const SCRAPING_PROXY_BASE =
  process.env.REACT_APP_SCRAPING_PROXY_URL ??
  process.env.REACT_APP_BACKEND_URL;

const scrapingProxyConfig = { baseURL: SCRAPING_PROXY_BASE };

const isLocalScrapingHost = Boolean(process.env.REACT_APP_SCRAPING_PROXY_URL);

const scrapingPath = (relativePath) =>
  isLocalScrapingHost ? relativePath : `scraping-proxy/${relativePath}`;

// ==================== PROJECTS (shared) ====================

export function useProjectsData() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await AxiosInstance.get(
        scrapingPath("projects"),
        scrapingProxyConfig,
      );
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const keywordProjects = projects.filter((p) =>
    Array.isArray(p.scraper_type) && p.scraper_type.includes("keyword"),
  );
  const filingProjects = projects.filter((p) =>
    Array.isArray(p.scraper_type) && p.scraper_type.includes("filing"),
  );

  return {
    projects,
    keywordProjects,
    filingProjects,
    loading,
    error,
    refetch: fetchProjects,
  };
}

// ==================== KEYWORDS ====================

export function useKeywordsData() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await AxiosInstance.get(
        scrapingPath("keywords/all"),
        scrapingProxyConfig,
      );
      setRows(res.data.data || []);
    } catch (err) {
      console.error("Error fetching keywords:", err);
      setError(err.response?.data?.error || err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addKeyword = async (keyword, projectName) => {
    try {
      const response = await AxiosInstance.post(
        scrapingPath("keywords"),
        {
          keyword: keyword.trim(),
          project_name: projectName.trim(),
        },
        scrapingProxyConfig,
      );
      const createdKeyword = response.data;
      setRows((prev) => [createdKeyword, ...prev]);
      return createdKeyword;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const updateKeyword = async (rowId, values) => {
    try {
      const response = await AxiosInstance.put(
        scrapingPath(`keywords/${rowId}`),
        values,
        scrapingProxyConfig,
      );
      const updatedKeyword = response.data;
      setRows((prev) =>
        prev.map((row) => (row._id === rowId ? updatedKeyword : row)),
      );
      return updatedKeyword;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const deleteKeyword = async (rowId) => {
    try {
      await AxiosInstance.delete(
        scrapingPath(`keywords/${rowId}`),
        scrapingProxyConfig,
      );
      setRows((prev) => prev.filter((row) => row._id !== rowId));
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const clearError = () => setError(null);

  return {
    rows,
    loading,
    error,
    setError,
    clearError,
    addKeyword,
    updateKeyword,
    deleteKeyword,
    refetch: fetchData,
  };
}

// ==================== FILING PAGES ====================

export function useFilingsData() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await AxiosInstance.get(
        scrapingPath("filings/all"),
        scrapingProxyConfig,
      );
      setRows(res.data.data || []);
    } catch (err) {
      console.error("Error fetching filings:", err);
      setError(err.response?.data?.error || err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addFiling = async (label, url, projectName, active = true) => {
    try {
      const response = await AxiosInstance.post(
        scrapingPath("filings"),
        {
          label: label.trim(),
          url: url.trim(),
          project_name: projectName.trim(),
          active,
        },
        scrapingProxyConfig,
      );
      const created = response.data;
      setRows((prev) => [created, ...prev]);
      return created;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const updateFiling = async (rowId, values) => {
    try {
      const response = await AxiosInstance.put(
        scrapingPath(`filings/${rowId}`),
        values,
        scrapingProxyConfig,
      );
      const updated = response.data;
      setRows((prev) =>
        prev.map((row) => (row._id === rowId ? updated : row)),
      );
      return updated;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const deleteFiling = async (rowId) => {
    try {
      await AxiosInstance.delete(
        scrapingPath(`filings/${rowId}`),
        scrapingProxyConfig,
      );
      setRows((prev) => prev.filter((row) => row._id !== rowId));
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const clearError = () => setError(null);

  return {
    rows,
    loading,
    error,
    setError,
    clearError,
    addFiling,
    updateFiling,
    deleteFiling,
    refetch: fetchData,
  };
}

// ==================== SCRAPERS CONFIG ====================

export function useScrapersData() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await AxiosInstance.get(
        scrapingPath("scrapers"),
        scrapingProxyConfig,
      );
      setRows(res.data.data || []);
    } catch (err) {
      console.error("Error fetching scrapers:", err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateScraper = async (rowId, values) => {
    try {
      const response = await AxiosInstance.put(
        scrapingPath(`scrapers/${rowId}`),
        values,
        scrapingProxyConfig,
      );
      const updated = response.data;
      setRows((prev) =>
        prev.map((row) => (row._id === rowId ? updated : row)),
      );
      return updated;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const clearError = () => setError(null);

  return {
    rows,
    loading,
    error,
    setError,
    clearError,
    updateScraper,
    refetch: fetchData,
  };
}
